import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { getShortenedUrl } from '../services/url-shortener'
import { CalculatorContext } from '../calculator/context'
import arrowRight from '../assets/images/arrow-right.png'
import { useLuminate } from '../hooks/use-luminate'
import ReCAPTCHA from 'react-google-recaptcha'

import { sendToDataLayer } from '../util'

export const ResultsSignup = ({ tonnage }) => {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [emailOptIn, setEmailOptIn] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState(null)
  const [recaptchaError, setRecaptchaError] = useState(false)

  const recaptchaRef = React.useRef(null)

  const handleFirstNameChange = e => setFirstName(e.target.value)
  const handleEmailChange = e => setEmail(e.target.value)
  const handleEmailOptIn = e => setEmailOptIn(!emailOptIn)

  const [luminate, setLuminateData] = useLuminate()
  const showForm = !luminate.response.success

  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY

  React.useEffect(() => {
    if (luminate.error && recaptchaRef.current) {
      recaptchaRef.current.reset()
      setRecaptchaToken(null)
    }
  }, [luminate.error])

  return (
    <CalculatorContext.Consumer>
      {({ locale, userLocation }) => (
        <div className="ciicc-section ciicc-luminate-survey">
          <div className="ciicc-wrap -narrow">
            <h2 className="text-header-primary-color semibold font-24">
              <FormattedMessage id="results.signup.title" />
            </h2>
            <form
              method="post"
              onSubmit={async e => {
                e.preventDefault()

                if (recaptchaSiteKey && !recaptchaToken) {
                  setRecaptchaError(true)
                  return
                }

                const currentPath = window.location.href
                const { url } = await getShortenedUrl(currentPath)
                const shortUrl = url ?? currentPath

                setLuminateData({
                  locale,
                  emailOptIn,
                  countryCode: userLocation.countryCode,
                  email,
                  firstName,
                  lastName: null,
                  tonnage,
                  longUrl: currentPath,
                  shortUrl
                })
              }}
            >
              <LuminateSuccess
                luminate={luminate}
                locale={locale}
                userLocation={userLocation}
              />

              {showForm && (
                <>
                  <div className="ciicc-luminate-survey__form">
                    <div className="ciicc-luminate-survey__field">
                      <input
                        className={`ciicc-input text -light${firstName.length > 0 ? ' has-value' : ''
                          }`}
                        id="first-name"
                        name="first-name"
                        required
                        type="text"
                        value={firstName}
                        onChange={handleFirstNameChange}
                        onInvalid={e => e.currentTarget.classList.add('-invalid')}
                      />
                      <label
                        className="ciicc-luminate-survey__label text -light"
                        htmlFor="first-name"
                      >
                        <FormattedMessage id="results.signup.name" />
                      </label>
                    </div>
                    <div className="ciicc-luminate-survey__field">
                      <input
                        className={`ciicc-input text -light${email.length > 0 ? ' has-value' : ''
                          }`}
                        id="email"
                        name="email"
                        required
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        onInvalid={e => e.currentTarget.classList.add('-invalid')}
                      />
                      <label
                        className="ciicc-luminate-survey__label text -light"
                        htmlFor="email"
                      >
                        <FormattedMessage id="results.signup.label" />
                      </label>
                    </div>

                    <LuminateError luminate={luminate} />
                  </div>

                  <label
                    className="ciicc-luminate-survey__checkbox text -light"
                    htmlFor="opt-in"
                  >
                    <input
                      id="opt-in"
                      name="opt-in"
                      type="checkbox"
                      onChange={handleEmailOptIn}
                      checked={emailOptIn}
                    />
                    <FormattedMessage id="results.signup.optin.label" />
                  </label>

                  <div className="ciicc-luminate-survey__submission-row" style={{ marginTop: '25px', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'center', flexWrap: 'wrap', gap: '20px' }}>
                    {recaptchaSiteKey && (
                      <div className="ciicc-luminate-survey__recaptcha" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={recaptchaSiteKey}
                          onChange={token => {
                            setRecaptchaToken(token)
                            if (token) setRecaptchaError(false)
                          }}
                        />
                        {recaptchaError && (
                          <p
                            className="error-message"
                            style={{
                              color: '#d32f2f',
                              fontSize: '14px',
                              marginTop: '5px',
                              marginBottom: '5px',
                              textAlign: 'center',
                              width: '100%'
                            }}
                          >
                            <FormattedMessage
                              id="results.signup.recaptcha.error"
                              defaultMessage="Please verify that you are not a robot."
                            />
                          </p>
                        )}
                      </div>
                    )}

                    <button
                      className="ciicc-button -primary"
                      type="submit"
                      style={{ margin: 0, height: '76px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <FormattedMessage id="results.signup.submit" />
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </CalculatorContext.Consumer>
  )
}

const LuminateSuccess = ({ luminate, locale, userLocation }) => {
  if (luminate.response && luminate.response.success) {
    sendToDataLayer(
      locale,
      userLocation,
      'Submit Email Address',
      'Send my results'
    )

    return (
      <p className="centered-text">
        <FormattedMessage id="checkout.confirmation.email_optin.confirmation" />
      </p>
    )
  }

  return null
}

const LuminateError = ({ luminate }) =>
  luminate.error && (
    <p
      className="centered-text"
      style={{ order: 1, width: '100%', marginTop: '1em' }}
    >
      <FormattedMessage id="checkout.confirmation.email_optin.error" />
    </p>
  )
